import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import { Text, Flex, Select, TextField, FormField, Box } from '@scale/scaleui-radix';
import { CompassCardComponent, CompassCardComponentProps } from '@scale/compass-ui/types';
import {
  Metric,
  ALL_METRICS,
  Config,
  PROJECT_TYPES,
  ExcludedMetricsByProjectType,
  ProjectType,
  MetricToLabelMap,
  TemplaticnessSchema,
  ALL_CONFIGS,
} from '@scale/shared/types/quality';
import {
  isTaskIdCompatibleColumn,
  isPromptCompatibleColumn,
  PromptDiversityContext,
} from '@scale/genai-compass-shared/cards/prompt_diversity';
import { renderConfigInputs } from './PromptDiversityConfigs';
import usePromptDiversityCard from 'react-scale/corp/views/DataCompassCardsV2/transformation/PromptDiversity/usePromptDiversityCard';
import { Column } from '@scale/compass-shared/types';

function getMetricLabel(metric: Metric): string {
  const label: string = MetricToLabelMap[metric];
  if (_.isUndefined(label)) {
    return 'Unknown Metric';
  }
  return label;
}

function getAvailableMetrics(projectType: ProjectType | string): Metric[] {
  const excluded = ExcludedMetricsByProjectType[(projectType as ProjectType) ?? 'general'] ?? [];
  return ALL_METRICS.filter((m: Metric) => !excluded.includes(m));
}

const PromptDiversityCardComponent: CompassCardComponent<PromptDiversityContext> = ({
  context,
  updateCardContext,
  updateCardCanSubmit,
  previousCardData,
}: CompassCardComponentProps<PromptDiversityContext>) => {
  const {
    projectId,
    setProjectId,
    projectIdIsEditable,
    projectType,
    setProjectType,
    taskIdColumn,
    setTaskIdColumn,
    promptColumn,
    setPromptColumn,
    diversityConfig,
    setDiversityConfig,
  } = usePromptDiversityCard(context, updateCardContext, updateCardCanSubmit);

  // ---------------- Diversity Metric Config Info  ----------------
  const handleConfigChange = useCallback(
    (updatedConfig: Config) => {
      (setDiversityConfig as unknown as (config: Config) => void)(updatedConfig);
    },
    [setDiversityConfig],
  );

  const buildDefaultConfigForMetric = useCallback(
    (metric: Metric): Config => {
      if (metric === Metric.Templaticness) {
        return TemplaticnessSchema.parse({
          metric,
          metricConfig: { projectId },
        });
      }

      const defaultConfig = ALL_CONFIGS.find(config => config.metric === metric) ?? ALL_CONFIGS[0]!;
      return _.cloneDeep(defaultConfig);
    },
    [projectId],
  );

  const handleMetricChange = useCallback(
    (metric: Metric) => {
      if (diversityConfig?.metric === metric) {
        return;
      }
      const newConfig = buildDefaultConfigForMetric(metric);
      handleConfigChange(newConfig);
    },
    [buildDefaultConfigForMetric, diversityConfig?.metric, handleConfigChange],
  );

  const availableMetrics: Metric[] = useMemo(() => getAvailableMetrics(projectType), [projectType]);

  type SelectOption = {
    title: string;
    value: string;
  };

  const taskIdColumnOptions: SelectOption[] = useMemo(() => {
    const compatibleColumns = previousCardData?.columns?.filter(isTaskIdCompatibleColumn) ?? [];
    return compatibleColumns.map(
      ({ id, header }: Column): SelectOption => ({
        title: header ?? id,
        value: id,
      }),
    );
  }, [previousCardData?.columns]);

  const promptColumnOptions: SelectOption[] = useMemo(() => {
    const compatibleColumns = previousCardData?.columns?.filter(isPromptCompatibleColumn) ?? [];
    return compatibleColumns.map(
      ({ id, header }: Column): SelectOption => ({
        title: header ?? id,
        value: id,
      }),
    );
  }, [previousCardData?.columns]);

  return (
    <Flex direction="column" gap="4" width="100%">
      <Flex direction="column" gap="2" width="100%">
        <Text size="3" weight="bold">
          Project Information
        </Text>
        <Flex direction="row" gap="2" justify="start" align="start">
          {projectIdIsEditable && (
            <Box width="45%" maxWidth="400px">
              <FormField.Root>
                <FormField.Label htmlFor="project-id">Project ID</FormField.Label>
                <TextField.Root
                  id="project-id"
                  value={projectId}
                  onChange={e => setProjectId(e.target.value)}
                  placeholder="Enter Project ID"
                  color={_.isEmpty(projectId) ? 'red' : undefined}
                />
                <Text size="1" color="gray">
                  Used for cost attribution
                </Text>
                {_.isEmpty(projectId) && (
                  <Text size="2" color="red">
                    Project ID is required
                  </Text>
                )}
              </FormField.Root>
            </Box>
          )}

          <Box width="45%" maxWidth="400px">
            <FormField.Root>
              <FormField.Label htmlFor="project-type">Diversity Project Type</FormField.Label>
              <Select.Root
                value={projectType}
                onValueChange={(value: string): void => setProjectType(value as ProjectType)}
              >
                <Select.Trigger />
                <Select.Content>
                  {PROJECT_TYPES.map(type => (
                    <Select.Item key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
              <Text size="1" color="gray">
                Used for metric computation
              </Text>
            </FormField.Root>
          </Box>
        </Flex>
      </Flex>

      <Flex direction="column" gap="2" width="100%">
        <Text size="3" weight="bold">
          Target Columns
        </Text>
        <Flex direction="row" gap="2" justify="start" align="start">
          <Box width="45%" maxWidth="400px">
            <FormField.Root>
              <FormField.Label htmlFor="task-id-column">Task ID</FormField.Label>
              <Select.Root value={taskIdColumn} onValueChange={setTaskIdColumn} required>
                <Select.Trigger color={_.isEmpty(taskIdColumn) ? 'red' : undefined} />
                <Select.Content>
                  {taskIdColumnOptions.map(option => (
                    <Select.Item key={option.value} value={option.value}>
                      {option.title}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
              {_.isEmpty(taskIdColumn) && (
                <Text size="2" color="red">
                  Task ID column is required
                </Text>
              )}
            </FormField.Root>
          </Box>

          <Box width="45%" maxWidth="400px">
            <FormField.Root>
              <FormField.Label htmlFor="prompt-column">Prompt</FormField.Label>
              <Select.Root value={promptColumn} onValueChange={setPromptColumn} required>
                <Select.Trigger color={_.isEmpty(promptColumn) ? 'red' : undefined} />
                <Select.Content>
                  {promptColumnOptions.map(option => (
                    <Select.Item key={option.value} value={option.value}>
                      {option.title}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
              {_.isEmpty(promptColumn) && (
                <Text size="2" color="red">
                  Prompt column is required
                </Text>
              )}
            </FormField.Root>
          </Box>
        </Flex>
      </Flex>

      <Flex direction="column" gap="2" width="100%">
        <Text size="3" weight="bold">
          Diversity Metric Config
        </Text>

        <Flex direction="row" gap="2" justify="start" align="start">
          <Box width="45%" maxWidth="400px">
            <FormField.Root>
              <FormField.Label htmlFor="diversity-metric">Metric</FormField.Label>
              <Select.Root
                value={diversityConfig?.metric ?? ''}
                onValueChange={(value: string): void => handleMetricChange(value as Metric)}
                required
              >
                <Select.Trigger color={_.isEmpty(diversityConfig?.metric) ? 'red' : undefined} />
                <Select.Content>
                  {availableMetrics.map((metric: Metric) => (
                    <Select.Item key={metric} value={metric}>
                      {getMetricLabel(metric)}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
              {_.isEmpty(diversityConfig?.metric) && (
                <Text size="2" color="red">
                  Diversity metric is required
                </Text>
              )}
            </FormField.Root>
          </Box>
        </Flex>

        {diversityConfig && (
          <Box width="100%">{renderConfigInputs(diversityConfig, handleConfigChange)}</Box>
        )}
      </Flex>
    </Flex>
  );
};

export default PromptDiversityCardComponent;
